import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Car, ClipboardList, Edit3, Loader2, Plus, Save, Trash2, Wrench } from "lucide-react";
import { CalidadControlDocumentalPanel } from "../../../components/control-modificaciones/CalidadControlDocumentalPanel";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import { Iso900114001Filters } from "../../../components/shared/Iso900114001Filters";
import { Iso900114001IconButton } from "../../../components/shared/Iso900114001IconButton";
import { Iso900114001MasterDetailTable } from "../../../components/shared/Iso900114001MasterDetailTable";
import { Iso900114001ScrollableCell } from "../../../components/shared/Iso900114001ScrollableCell";
import { Iso900114001Input, Iso900114001Select, Iso900114001Textarea } from "../../../components/shared/Iso900114001Fields";
import { Iso900114001Modal } from "../../../components/shared/Iso900114001Modal";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import { getCertificationRequestError } from "../../../components/shared/request";
import { useIso900114001DebouncedValue, useIso900114001LatestRequest } from "../../../components/shared/useIso900114001FilteredRequest";
import {
  deleteIntervencion,
  deleteVehiculo,
  getMantenimientoVehiculos,
  mantenimientoVehiculosControlEndpoint,
  saveIntervencion,
  saveVehiculo,
} from "../api/mantenimiento-vehiculos";
import {
  categoriaItvLabels,
  emptyIntervention,
  emptyVehicle,
  type EstadoIntervencionProgramada,
  type IntervencionPayload,
  type MantenimientoVehiculo,
  type MantenimientoVehiculoIntervencion,
  type MantenimientoVehiculoIntervencionProgramada,
  type MantenimientoVehiculoPayload,
  type MantenimientoVehiculosResponse,
  type TipoIntervencionProgramada,
} from "../types/mantenimiento-vehiculos";

type Props = { onBack: () => void };
type Tab = "mantenimiento" | "control";
type ScheduledFilters = {
  vehiculoId: "" | number;
  tipo: "" | TipoIntervencionProgramada;
  estado: "" | EstadoIntervencionProgramada;
};

export function CalidadMantenimientoVehiculosSection({ onBack }: Props) {
  const [tab, setTab] = useState<Tab>("mantenimiento");
  const [data, setData] = useState<MantenimientoVehiculosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({ search: "", responsable: "", combustible: "", anio: "" });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [scheduledFilters, setScheduledFilters] = useState<ScheduledFilters>({ vehiculoId: "", tipo: "", estado: "" });
  const [vehicleForm, setVehicleForm] = useState<{
    open: boolean;
    id: number | null;
    value: MantenimientoVehiculoPayload;
  }>({ open: false, id: null, value: emptyVehicle });
  const [interventionForm, setInterventionForm] = useState<{
    open: boolean;
    id: number | null;
    vehicleId: number | null;
    fromSchedule: boolean;
    value: IntervencionPayload;
  }>({ open: false, id: null, vehicleId: null, fromSchedule: false, value: emptyIntervention });
  const [deleteTarget, setDeleteTarget] = useState<{
    kind: "vehicle" | "intervention";
    id: number;
    label: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso900114001LatestRequest();

  const load = useCallback(async (force = false) => {
    const params = {
      search: debouncedSearch || undefined,
      responsable: filters.responsable || undefined,
      combustible: filters.combustible || undefined,
      anio: filters.anio || undefined,
    };
    const requestKey = JSON.stringify(params);

    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(
        requestKey,
        (signal) => getMantenimientoVehiculos(params, signal),
        force,
      );
      if (!response) return;
      setData(response);
      setSelectedId((current) => (
        response.data.vehiculos.some((row) => row.id === current)
          ? current
          : response.data.vehiculos[0]?.id ?? null
      ));
    } catch (requestError) {
      setError(getCertificationRequestError(requestError, "No se pudo cargar el mantenimiento de vehículos."));
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [debouncedSearch, filters.anio, filters.combustible, filters.responsable, hasActiveRequest, runLatest]);

  useEffect(() => {
    void load(false);
  }, [load]);

  const selected = useMemo(
    () => data?.data.vehiculos.find((row) => row.id === selectedId) ?? null,
    [data, selectedId],
  );

  const openVehicle = (row?: MantenimientoVehiculo) => {
    setFormError(null);
    setVehicleForm({
      open: true,
      id: row?.id ?? null,
      value: row
        ? {
            matricula: row.matricula ?? "",
            marca_modelo: row.marca_modelo ?? "",
            responsable: row.responsable ?? "",
            regimen: row.regimen ?? "",
            tipo_combustible: row.tipo_combustible ?? "",
            seguro: row.seguro ?? "",
            fecha_matriculacion: row.fecha_matriculacion,
            categoria_itv: row.categoria_itv ?? "M1",
            fecha_itv: row.fecha_itv,
            fecha_mantenimiento_anual: row.fecha_mantenimiento_anual,
          }
        : { ...emptyVehicle },
    });
  };

  const openIntervention = (row?: MantenimientoVehiculoIntervencion) => {
    if (!selected) return;
    setFormError(null);
    setInterventionForm({
      open: true,
      id: row?.id ?? null,
      vehicleId: selected.id,
      fromSchedule: false,
      value: row
        ? { fecha: row.fecha, intervencion: row.intervencion }
        : { ...emptyIntervention },
    });
  };

  const openScheduledIntervention = (item: MantenimientoVehiculoIntervencionProgramada) => {
    setSelectedId(item.vehiculo_id);
    setFormError(null);
    setInterventionForm({
      open: true,
      id: null,
      vehicleId: item.vehiculo_id,
      fromSchedule: true,
      value: { fecha: item.fecha, intervencion: item.titulo },
    });
  };

  const submitVehicle = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await saveVehiculo(vehicleForm.id, vehicleForm.value);
      setVehicleForm({ open: false, id: null, value: emptyVehicle });
      await load(true);
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar el vehículo."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitIntervention = async () => {
    if (interventionForm.vehicleId === null) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await saveIntervencion(interventionForm.vehicleId, interventionForm.id, interventionForm.value);
      setInterventionForm({ open: false, id: null, vehicleId: null, fromSchedule: false, value: emptyIntervention });
      await load(true);
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar la intervención."));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      if (deleteTarget.kind === "vehicle") {
        await deleteVehiculo(deleteTarget.id);
      } else {
        await deleteIntervencion(deleteTarget.id);
      }
      setDeleteTarget(null);
      await load(true);
    } finally {
      setSubmitting(false);
    }
  };

  const vehicles = data?.data.vehiculos ?? [];
  const scheduled = data?.data.proximas_intervenciones ?? [];
  const controls = data?.data.control_modificaciones ?? [];
  const filteredScheduled = useMemo(() => scheduled.filter((item) => (
    (scheduledFilters.vehiculoId === "" || item.vehiculo_id === scheduledFilters.vehiculoId)
    && (scheduledFilters.tipo === "" || item.tipo === scheduledFilters.tipo)
    && (scheduledFilters.estado === "" || item.estado === scheduledFilters.estado)
  )), [scheduled, scheduledFilters]);

  return (
    <section className="iso9001-section-stack">
      <Iso900114001SectionHeader
        eyebrow="Calidad y medioambiente"
        title="Mantenimiento de vehículos"
        description="Gestiona los vehículos, su historial y las próximas ITV o mantenimientos anuales pendientes. La hoja de consumo de gasolina no forma parte de esta sección."
        onBack={onBack}
        onRefresh={() => void load(true)}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard icon={Car} label="Vehículos" value={data?.meta.total_vehiculos ?? 0} />
        <Iso900114001OverviewCard icon={Wrench} label="Intervenciones" value={data?.meta.total_intervenciones ?? 0} />
        <Iso900114001OverviewCard icon={CalendarClock} label="Próximas intervenciones" value={data?.meta.total_intervenciones_pendientes ?? 0} />
        <Iso900114001OverviewCard icon={ClipboardList} label="Control documental" value={controls.length} />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "mantenimiento", label: "Mantenimiento de vehículos", count: vehicles.length },
          { id: "control", label: "Control documental", count: controls.length },
        ]}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Mantenimiento de vehículos"
      />

      {tab === "control" ? (
        <CalidadControlDocumentalPanel
          rows={controls}
          loading={loading}
          error={error}
          baseEndpoint={mantenimientoVehiculosControlEndpoint}
          onReload={() => load(true)}
        />
      ) : (
        <>
          <Iso900114001Filters
            title="Buscar vehículos e intervenciones"
            count={vehicles.length}
            onReset={() => setFilters({ search: "", responsable: "", combustible: "", anio: "" })}
          >
            <Iso900114001Input
              label="Búsqueda"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
              placeholder="Matrícula, modelo, seguro o intervención"
            />
            <Iso900114001Select
              label="Responsable"
              value={filters.responsable}
              onChange={(event) => setFilters({ ...filters, responsable: event.target.value })}
            >
              <option value="">Todos</option>
              {(data?.filter_options.responsables ?? []).map((value) => <option key={value}>{value}</option>)}
            </Iso900114001Select>
            <Iso900114001Select
              label="Combustible"
              value={filters.combustible}
              onChange={(event) => setFilters({ ...filters, combustible: event.target.value })}
            >
              <option value="">Todos</option>
              {(data?.filter_options.combustibles ?? []).map((value) => <option key={value}>{value}</option>)}
            </Iso900114001Select>
            <Iso900114001Select
              label="Año de intervención"
              value={filters.anio}
              onChange={(event) => setFilters({ ...filters, anio: event.target.value })}
            >
              <option value="">Todos</option>
              {(data?.filter_options.anios ?? []).map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </Iso900114001Select>
          </Iso900114001Filters>

          {error ? <div className="reqleg-inline-error">{error}</div> : null}

          <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="iso9001-section-eyebrow">Planificación y seguimiento</div>
                <h3 className="text-lg font-black text-slate-950">Próximas intervenciones</h3>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Se muestran únicamente intervenciones de hoy en adelante: planificación automática y actuaciones futuras ya registradas.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs font-black">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-700">
                  {data?.meta.total_intervenciones_pendientes ?? 0} próximas
                </span>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-amber-700">
                  {data?.meta.intervenciones_proximas ?? 0} en 60 días
                </span>
              </div>
            </div>

            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <Iso900114001Select
                label="Vehículo"
                value={scheduledFilters.vehiculoId}
                onChange={(event) => setScheduledFilters((current) => ({
                  ...current,
                  vehiculoId: event.target.value === "" ? "" : Number(event.target.value),
                }))}
              >
                <option value="">Todos</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.matricula || "Sin matrícula"} · {vehicle.marca_modelo || "Sin modelo"}
                  </option>
                ))}
              </Iso900114001Select>
              <Iso900114001Select
                label="Tipo de intervención"
                value={scheduledFilters.tipo}
                onChange={(event) => setScheduledFilters((current) => ({
                  ...current,
                  tipo: event.target.value as ScheduledFilters["tipo"],
                }))}
              >
                <option value="">Todos</option>
                <option value="itv">ITV</option>
                <option value="mantenimiento_anual">Mantenimiento anual</option>
                <option value="otra">Otras registradas</option>
              </Iso900114001Select>
              <Iso900114001Select
                label="Estado de planificación"
                value={scheduledFilters.estado}
                onChange={(event) => setScheduledFilters((current) => ({
                  ...current,
                  estado: event.target.value as ScheduledFilters["estado"],
                }))}
              >
                <option value="">Todos</option>
                <option value="proxima">Próximas 60 días</option>
                <option value="futura">Futuras</option>
              </Iso900114001Select>
            </div>

            {filteredScheduled.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm font-semibold text-slate-500">
                No hay intervenciones que coincidan con los filtros actuales.
              </div>
            ) : (
              <div className="max-h-[25rem] overflow-y-auto pr-1">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {filteredScheduled.map((item) => (
                    <ScheduledInterventionCard
                      key={item.id}
                      item={item}
                      onRegister={item.registrada ? undefined : () => openScheduledIntervention(item)}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <Iso900114001MasterDetailTable
            eyebrow="Mantenimiento"
            title="Vehículos e intervenciones"
            countLabel={`${vehicles.length} vehículos`}
            createLabel="Nuevo vehículo"
            items={vehicles.map((row) => ({
              id: row.id,
              label: `${row.matricula || "Sin matrícula"} · ${row.marca_modelo || "Sin modelo"}`,
              shortLabel: row.matricula || "—",
              title: `${row.matricula || "Sin matrícula"} · ${row.marca_modelo || "Sin modelo"}`,
              count: row.intervenciones.length,
            }))}
            selectedId={selectedId}
            loading={loading}
            emptyText="No hay vehículos."
            onCreate={() => openVehicle()}
            onSelect={(id) => setSelectedId(Number(id))}
          >
            {!selected ? (
              <div className="iso9001-master-detail-empty iso9001-master-detail-empty-large">
                Selecciona o crea un vehículo.
              </div>
            ) : (
              <>
                <div className="iso9001-detail-heading">
                  <div>
                    <div className="iso9001-section-eyebrow">Vehículo seleccionado</div>
                    <h3>{selected.matricula || "Sin matrícula"}</h3>
                    <p>{selected.marca_modelo || "Sin marca y modelo"}</p>
                  </div>
                  <div className="calidad-table-actions-row">
                    <Iso900114001IconButton
                      label="Editar vehículo"
                      icon={<Edit3 size={15} />}
                      onClick={() => openVehicle(selected)}
                    />
                    <Iso900114001IconButton
                      label="Eliminar vehículo"
                      icon={<Trash2 size={15} />}
                      danger
                      onClick={() => setDeleteTarget({
                        kind: "vehicle",
                        id: selected.id,
                        label: selected.matricula || "vehículo",
                      })}
                    />
                  </div>
                </div>

                <div className="iso9001-vehicle-detail-grid">
                  <VehicleDetail label="Matrícula" value={selected.matricula} />
                  <VehicleDetail label="Marca y modelo" value={selected.marca_modelo} />
                  <VehicleDetail label="Responsable" value={selected.responsable} />
                  <VehicleDetail label="Fecha de matriculación" value={formatDate(selected.fecha_matriculacion)} />
                  <VehicleDetail label="Categoría ITV" value={selected.categoria_itv ? categoriaItvLabels[selected.categoria_itv] : categoriaItvLabels.M1} />
                  <VehicleDetail label="ITV de referencia" value={formatDate(selected.fecha_itv)} />
                  <VehicleDetail label="Mantenimiento anual" value={formatDate(selected.fecha_mantenimiento_anual)} />
                  <VehicleDetail label="Régimen" value={selected.regimen} />
                  <VehicleDetail label="Combustible" value={selected.tipo_combustible} />
                  <VehicleDetail label="Seguro" value={selected.seguro} />
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <CalendarClock size={17} className="text-emerald-700" />
                    <h4 className="font-black text-slate-950">Próximas intervenciones de este vehículo</h4>
                  </div>
                  {selected.proximas_intervenciones.length === 0 ? (
                    <p className="text-sm font-semibold text-slate-500">No hay próximas intervenciones para este vehículo.</p>
                  ) : (
                    <div className="max-h-[20rem] overflow-y-auto pr-1">
                      <div className="grid gap-2 sm:grid-cols-2">
                        {selected.proximas_intervenciones.map((item) => (
                          <ScheduledInterventionCard
                            key={item.id}
                            item={item}
                            compact
                            onRegister={item.registrada ? undefined : () => openScheduledIntervention(item)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="iso9001-subtable-heading">
                  <h4>Intervenciones registradas</h4>
                  <button type="button" className="calidad-primary-button" onClick={() => openIntervention()}>
                    <Plus size={15} />
                    Añadir intervención
                  </button>
                </div>

                <div className="iso9001-crud-scroll">
                  <table className="calidad-table iso9001-crud-table">
                    <thead>
                      <tr>
                        <th className="iso9001-actions-column" style={{ textAlign: "left" }}>Acciones</th>
                        <th>Fecha</th>
                        <th className="iso9001-extra-long-column">Intervención</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.intervenciones.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="iso9001-empty-table-cell">
                            No hay intervenciones para este vehículo.
                          </td>
                        </tr>
                      ) : selected.intervenciones.map((row) => (
                        <tr key={row.id}>
                          <td className="iso9001-actions-column" style={{ textAlign: "left" }}>
                            <div className="calidad-table-actions-row" style={{ justifyContent: "flex-start" }}>
                              <Iso900114001IconButton
                                label="Editar intervención"
                                icon={<Edit3 size={14} />}
                                onClick={() => openIntervention(row)}
                              />
                              <Iso900114001IconButton
                                label="Eliminar intervención"
                                icon={<Trash2 size={14} />}
                                danger
                                onClick={() => setDeleteTarget({
                                  kind: "intervention",
                                  id: row.id,
                                  label: row.intervencion,
                                })}
                              />
                            </div>
                          </td>
                          <td>{row.fecha || "—"}</td>
                          <td className="iso9001-extra-long-column">
                            <Iso900114001ScrollableCell title={row.intervencion}>
                              {row.intervencion}
                            </Iso900114001ScrollableCell>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Iso900114001MasterDetailTable>
        </>
      )}

      <Iso900114001Modal
        open={vehicleForm.open}
        title={vehicleForm.id ? "Editar vehículo" : "Nuevo vehículo"}
        submitting={submitting}
        error={formError}
        onClose={() => setVehicleForm({ open: false, id: null, value: emptyVehicle })}
        onSubmit={() => void submitVehicle()}
        footer={(
          <>
            <button type="button" className="calidad-back-button" onClick={() => setVehicleForm({ open: false, id: null, value: emptyVehicle })}>
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        )}
      >
        <Iso900114001Input label="Matrícula" value={vehicleForm.value.matricula ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, matricula: event.target.value } })} />
        <Iso900114001Input label="Marca / modelo" value={vehicleForm.value.marca_modelo ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, marca_modelo: event.target.value } })} />
        <Iso900114001Input label="Responsable" value={vehicleForm.value.responsable ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, responsable: event.target.value } })} />
        <Iso900114001Input label="Régimen" value={vehicleForm.value.regimen ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, regimen: event.target.value } })} />
        <Iso900114001Input label="Tipo de combustible" value={vehicleForm.value.tipo_combustible ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, tipo_combustible: event.target.value } })} />
        <Iso900114001Input label="Seguro" value={vehicleForm.value.seguro ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, seguro: event.target.value } })} />
        <Iso900114001Input label="Fecha de matriculación" type="date" value={vehicleForm.value.fecha_matriculacion ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, fecha_matriculacion: event.target.value || null } })} />
        <Iso900114001Select label="Categoría ITV" value={vehicleForm.value.categoria_itv ?? "M1"} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, categoria_itv: event.target.value as MantenimientoVehiculoPayload["categoria_itv"] } })}>
          <option value="M1">{categoriaItvLabels.M1}</option>
          <option value="N1">{categoriaItvLabels.N1}</option>
        </Iso900114001Select>
        <Iso900114001Input label="Fecha ITV de referencia (no programa)" type="date" value={vehicleForm.value.fecha_itv ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, fecha_itv: event.target.value || null } })} />
        <Iso900114001Input label="Fecha de referencia del mantenimiento anual" type="date" value={vehicleForm.value.fecha_mantenimiento_anual ?? ""} onChange={(event) => setVehicleForm({ ...vehicleForm, value: { ...vehicleForm.value, fecha_mantenimiento_anual: event.target.value || null } })} />
      </Iso900114001Modal>

      <Iso900114001Modal
        open={interventionForm.open}
        title={interventionForm.id ? "Editar intervención" : interventionForm.fromSchedule ? "Registrar intervención prevista" : "Nueva intervención"}
        submitting={submitting}
        error={formError}
        onClose={() => setInterventionForm({ open: false, id: null, vehicleId: null, fromSchedule: false, value: emptyIntervention })}
        onSubmit={() => void submitIntervention()}
        footer={(
          <>
            <button type="button" className="calidad-back-button" onClick={() => setInterventionForm({ open: false, id: null, vehicleId: null, fromSchedule: false, value: emptyIntervention })}>
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        )}
      >
        <Iso900114001Input label="Fecha" type="date" value={interventionForm.value.fecha ?? ""} onChange={(event) => setInterventionForm({ ...interventionForm, value: { ...interventionForm.value, fecha: event.target.value || null } })} />
        <Iso900114001Textarea label="Intervención" fullWidth required rows={5} value={interventionForm.value.intervencion} onChange={(event) => setInterventionForm({ ...interventionForm, value: { ...interventionForm.value, intervencion: event.target.value } })} />
      </Iso900114001Modal>

      <Iso900114001ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar registro"
        description={deleteTarget ? `Se eliminará ${deleteTarget.label}.` : ""}
        confirming={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void remove()}
      />
    </section>
  );
}

function ScheduledInterventionCard({
  item,
  compact = false,
  onRegister,
}: {
  item: MantenimientoVehiculoIntervencionProgramada;
  compact?: boolean;
  onRegister?: () => void;
}) {
  const tone = item.estado === "proxima"
    ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-emerald-200 bg-emerald-50 text-emerald-800";
  const status = item.dias_restantes === 0
    ? item.registrada ? "Registrada para hoy" : "Prevista para hoy"
    : item.registrada
      ? `Registrada · en ${item.dias_restantes} día(s)`
      : item.estado === "proxima"
        ? `Próxima · en ${item.dias_restantes} día(s)`
        : `Futura · en ${item.dias_restantes} día(s)`;

  return (
    <article className={`rounded-2xl border p-3 ${tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-black uppercase tracking-[0.12em]">{item.titulo}</div>
          {!compact ? (
            <div className="mt-1 truncate text-sm font-black text-slate-950">
              {item.matricula || "Sin matrícula"} · {item.marca_modelo || "Sin modelo"}
            </div>
          ) : null}
        </div>
        <CalendarClock size={17} className="shrink-0" />
      </div>
      <div className="mt-2 text-sm font-black">{formatDate(item.fecha)}</div>
      <div className="mt-1 text-xs font-bold opacity-80">{status}</div>
      {onRegister ? (
        <div className="mt-3 flex justify-start">
          <button
            type="button"
            className="calidad-primary-button px-3 py-1.5 text-xs"
            style={{ width: "auto" }}
            onClick={onRegister}
          >
            <Plus size={13} />
            Registrar intervención
          </button>
        </div>
      ) : (
        <div className="mt-2 text-xs font-black text-emerald-700">Intervención ya registrada</div>
      )}
    </article>
  );
}

function VehicleDetail({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="iso9001-vehicle-detail-item">
      <span>{label}</span>
      <strong title={value || undefined}>{value || "—"}</strong>
    </div>
  );
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Intl.DateTimeFormat("es-ES").format(new Date(`${value}T00:00:00`));
}
